/**
 * SavedReportsPanel — slide-over panel for managing saved reports.
 *
 * Features:
 * - Folder tree with nested folders (create, rename, delete)
 * - Reports organized inside folders + root level
 * - Create report from current view state
 * - Apply report (restores filters, sort, columns, chart)
 * - Link to Google Sheets URL + copy-to-clipboard TSV export for Sheets import
 * - Pin/unpin reports for quick access
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  X, Plus, FolderPlus, Folder, FolderOpen, FileText, Star, StarOff,
  ChevronRight, ChevronDown, MoreHorizontal, Trash2, Pencil,
  Link, Sheet, Download, Check, Copy,
} from 'lucide-react';
import { useSavedReportsStore } from '../../../stores/prioritization/savedReportsStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { JustMeTag, ReportAudienceField } from './ReportAudience';
import { audienceSentence, type ReportFolder, type ReportVisibility, type SavedReport } from '../../../types/prioritization/savedReports';
import type { FilterGroup, SortSpec } from '../../prioritization/item-list/hooks/useItemListState';
import type { ChartType } from '../../prioritization/item-list/components/chartTypes';
import type { QueryNode } from '../../../lib/pql';

// ============================================================================
// Types
// ============================================================================

interface CurrentViewState {
  filterGroups: FilterGroup[];
  sort: SortSpec[];
  groupBy: string | null;
  visibleColumns: string[];
  activeChart: ChartType;
  /** Present when the list is in PQL statement mode — saved so the report re-opens as the statement. */
  queryText?: string;
  queryAst?: QueryNode | null;
}

interface ExportRow {
  id: string;
  title?: string;
  itemCategory?: string;
  detailedState?: string;
  altitudeLevel?: number;
  source?: string;
  createdAt?: Date | string;
  [key: string]: unknown;
}

interface SavedReportsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentState: CurrentViewState;
  onApplyReport: (report: SavedReport) => void;
  /** Export rows for the current filtered view (used for CSV / Sheets export) */
  exportRows?: ExportRow[];
}

// ============================================================================
// Helpers
// ============================================================================

function generateCSV(rows: ExportRow[]): string {
  const headers = ['id', 'title', 'itemCategory', 'detailedState', 'altitudeLevel', 'source', 'createdAt'];
  const escape = (v: unknown) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ].join('\n');
}

function generateTSV(rows: ExportRow[]): string {
  const headers = ['id', 'title', 'itemCategory', 'detailedState', 'altitudeLevel', 'source', 'createdAt'];
  return [
    headers.join('\t'),
    ...rows.map((r) => headers.map((h) => String(r[h] ?? '')).join('\t')),
  ].join('\n');
}

// ============================================================================
// Inline rename input
// ============================================================================

function InlineRename({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (v: string) => void;
  onCancel: () => void;
}) {
  const [val, setVal] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.select(); }, []);
  return (
    <input
      ref={ref}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (val.trim()) onCommit(val.trim()); else onCancel(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); if (val.trim()) onCommit(val.trim()); }
        if (e.key === 'Escape') onCancel();
      }}
      className="flex-1 text-sm px-1.5 py-0.5 rounded border border-[var(--slate)] ring-1 ring-indigo-100 outline-none bg-[var(--bg-raised)]"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

// ============================================================================
// Report row
// ============================================================================

function ReportRow({
  report,
  onApply,
  onExportCSV,
  onCopyTSV,
  onDelete,
  onTogglePin,
  onLinkSheets,
}: {
  report: SavedReport;
  onApply: () => void;
  onExportCSV: () => void;
  onCopyTSV: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
  onLinkSheets: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleCopyTSV = () => {
    onCopyTSV();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    setMenuOpen(false);
  };

  const ruleCount = report.filterGroups.flatMap((g) => g.rules).length;

  return (
    <div className="group flex items-center gap-2 px-3 py-2 hover:bg-[var(--surface)] transition-colors cursor-pointer">
      <FileText className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
      <button
        onClick={onApply}
        className="flex-1 text-left"
      >
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 truncate text-sm font-medium text-[var(--text-pri)]">{report.name}</span>
          {report.visibility === 'private' && <JustMeTag />}
        </div>
        {report.description && (
          <div className="text-[11px] text-[var(--text-ter)] truncate">{report.description}</div>
        )}
        <div className="flex items-center gap-2 mt-0.5">
          {ruleCount > 0 ? (
            <span className="text-[10px] text-[var(--text-ter)] font-medium">
              {ruleCount} filter{ruleCount !== 1 ? 's' : ''}
            </span>
          ) : report.queryText ? (
            // A search the filters cannot show (NOT, brackets) has no rule count: show the search.
            <span className="min-w-0 truncate font-mono text-[10px] text-[var(--text-ter)]" title={report.queryText}>
              {report.queryText}
            </span>
          ) : null}
          {report.sheetsUrl && (
            <span className="text-[10px] text-[var(--green)] dark:text-[var(--text-ter)] font-medium flex items-center gap-0.5">
              <Sheet className="h-2.5 w-2.5" />
              Linked
            </span>
          )}
        </div>
      </button>

      {/* Actions (visible on hover) */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onTogglePin}
          className="p-1 text-[var(--text-ter)] hover:text-[var(--mustard)] rounded transition-colors"
          title={report.isPinned ? 'Unpin' : 'Pin'}
        >
          {report.isPinned ? <Star className="h-3.5 w-3.5 text-[var(--mustard)]" /> : <StarOff className="h-3.5 w-3.5" />}
        </button>
        <div ref={menuRef} className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)]/80 z-50 py-1 overflow-hidden">
              <button
                onClick={() => { onExportCSV(); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
              >
                <Download className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                Download CSV
              </button>
              <button
                onClick={handleCopyTSV}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-[var(--green)]" /> : <Copy className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
                {copied ? 'Copied!' : 'Copy for Google Sheets'}
              </button>
              {report.sheetsUrl ? (
                <a
                  href={report.sheetsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
                  onClick={() => setMenuOpen(false)}
                >
                  <Sheet className="h-3.5 w-3.5 text-[var(--green)]" />
                  Open in Sheets
                </a>
              ) : (
                <button
                  onClick={() => { onLinkSheets(); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
                >
                  <Link className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                  Link Google Sheet
                </button>
              )}
              <div className="my-1 mx-2 border-t border-[var(--rule)]" />
              <button
                onClick={() => { onDelete(); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Folder section
// ============================================================================

function FolderSection({
  folder,
  reports,
  onApply,
  onExportCSV,
  onCopyTSV,
  onDeleteReport,
  onTogglePin,
  onLinkSheets,
  onRenameFolder,
  onDeleteFolder,
  depth,
  childFolders,
}: {
  folder: ReportFolder;
  reports: SavedReport[];
  childFolders: ReportFolder[];
  onApply: (r: SavedReport) => void;
  onExportCSV: (r: SavedReport) => void;
  onCopyTSV: (r: SavedReport) => void;
  onDeleteReport: (id: string) => void;
  onTogglePin: (id: string) => void;
  onLinkSheets: (id: string) => void;
  onRenameFolder: (id: string, name: string) => void;
  onDeleteFolder: (id: string) => void;
  depth?: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const d = depth ?? 0;

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div style={{ paddingLeft: d * 12 }}>
      <div className="group flex items-center gap-1.5 px-2 py-1.5 hover:bg-[var(--surface)] transition-colors">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 flex-1 text-left"
        >
          {expanded
            ? <ChevronDown className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
            : <ChevronRight className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
          }
          {expanded
            ? <FolderOpen className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />
            : <Folder className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />
          }
          {renaming ? (
            <InlineRename
              initial={folder.name}
              onCommit={(v) => { onRenameFolder(folder.id, v); setRenaming(false); }}
              onCancel={() => setRenaming(false)}
            />
          ) : (
            <span className="text-sm font-semibold text-[var(--text-sec)] truncate">{folder.name}</span>
          )}
        </button>
        <div ref={menuRef} className="relative opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
            className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-40 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)]/80 z-50 py-1">
              <button
                onClick={() => { setRenaming(true); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
              >
                <Pencil className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                Rename
              </button>
              <button
                onClick={() => { onDeleteFolder(folder.id); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete folder
              </button>
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div>
          {childFolders.map((cf) => (
            <RecursiveFolderSection
              key={cf.id}
              folder={cf}
              depth={(d ?? 0) + 1}
              onApply={onApply}
              onExportCSV={onExportCSV}
              onCopyTSV={onCopyTSV}
              onDeleteReport={onDeleteReport}
              onTogglePin={onTogglePin}
              onLinkSheets={onLinkSheets}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
            />
          ))}
          {reports.map((r) => (
            <ReportRow
              key={r.id}
              report={r}
              onApply={() => onApply(r)}
              onExportCSV={() => onExportCSV(r)}
              onCopyTSV={() => onCopyTSV(r)}
              onDelete={() => onDeleteReport(r.id)}
              onTogglePin={() => onTogglePin(r.id)}
              onLinkSheets={() => onLinkSheets(r.id)}
            />
          ))}
          {reports.length === 0 && childFolders.length === 0 && (
            <div className="px-4 py-2 text-[11px] text-[var(--text-ter)] italic">No reports yet</div>
          )}
        </div>
      )}
    </div>
  );
}

// Recursive wrapper so folder sections can render child folders
function RecursiveFolderSection({
  folder,
  depth,
  ...handlers
}: {
  folder: ReportFolder;
  depth: number;
  onApply: (r: SavedReport) => void;
  onExportCSV: (r: SavedReport) => void;
  onCopyTSV: (r: SavedReport) => void;
  onDeleteReport: (id: string) => void;
  onTogglePin: (id: string) => void;
  onLinkSheets: (id: string) => void;
  onRenameFolder: (id: string, name: string) => void;
  onDeleteFolder: (id: string) => void;
}) {
  const store = useSavedReportsStore();
  const childFolders = store.getChildFolders(folder.id);
  const reports = store.getReportsInFolder(folder.id);

  return (
    <FolderSection
      folder={folder}
      reports={reports}
      childFolders={childFolders}
      depth={depth}
      {...handlers}
    />
  );
}

// ============================================================================
// Link to Sheets modal
// ============================================================================

function LinkSheetsModal({
  onConfirm,
  onCancel,
}: {
  onConfirm: (url: string) => void;
  onCancel: () => void;
}) {
  const [url, setUrl] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-bold text-[var(--text-pri)] mb-1">Link Google Sheet</h3>
        <p className="text-sm text-[var(--text-ter)] mb-4">
          Paste your Google Sheets URL. Use "Copy for Google Sheets" to copy the current data as TSV, then paste it into the sheet.
        </p>
        <input
          autoFocus
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/…"
          className="w-full px-3 py-2 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 mb-4"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && url.trim()) onConfirm(url.trim());
            if (e.key === 'Escape') onCancel();
          }}
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (url.trim()) onConfirm(url.trim()); }}
            disabled={!url.trim()}
            className="px-4 py-1.5 text-sm font-semibold text-white bg-[var(--green)] hover:bg-[var(--green)] transition-colors disabled:opacity-50"
          >
            Link Sheet
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Create Report modal
// ============================================================================

function CreateReportModal({
  folders,
  onConfirm,
  onCancel,
}: {
  folders: ReportFolder[];
  onConfirm: (name: string, description: string, folderId: string | null, visibility: ReportVisibility) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<ReportVisibility>('workspace');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-bold text-[var(--text-pri)] mb-4">Save Current View as Report</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[var(--text-sec)] block mb-1">Name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Engineering backlog — Q2"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) onConfirm(name.trim(), description, folderId, visibility);
                if (e.key === 'Escape') onCancel();
              }}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--text-sec)] block mb-1">Description (optional)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this report show?"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          {folders.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-[var(--text-sec)] block mb-1">Folder</label>
              <select
                value={folderId ?? ''}
                onChange={(e) => setFolderId(e.target.value || null)}
                className="w-full px-3 py-2 text-sm border border-[var(--rule)] outline-none focus:border-[var(--slate)]"
              >
                <option value="">No folder (root)</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}
          <ReportAudienceField value={visibility} onChange={setVisibility} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (name.trim()) onConfirm(name.trim(), description, folderId, visibility); }}
            disabled={!name.trim()}
            className="px-4 py-1.5 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors disabled:opacity-50"
          >
            Save Report
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main panel
// ============================================================================

export function SavedReportsPanel({
  isOpen,
  onClose,
  currentState,
  onApplyReport,
  exportRows = [],
}: SavedReportsPanelProps) {
  const store = useSavedReportsStore();
  const fetchFromSupabase = useSavedReportsStore((s) => s.fetchFromSupabase);
  const addNotification = useUIStore((s) => s.addNotification);
  const rootFolders = store.getRootFolders();
  const rootReports = store.getReportsInFolder(null);
  const pinnedReports = store.getPinnedReports();

  const [showCreateReport, setShowCreateReport] = useState(false);
  const [linkSheetsForId, setLinkSheetsForId] = useState<string | null>(null);

  // Fetch on first open. The dependency is the ACTION, not `store`: this
  // component subscribes to the whole store, so `store` is a new object after
  // every set — including the two this fetch performs — and depending on it
  // re-ran the fetch forever while the panel was open (net::ERR_INSUFFICIENT_-
  // RESOURCES, hundreds of thousands of requests). Actions are stable.
  useEffect(() => {
    if (isOpen) void fetchFromSupabase();
  }, [isOpen, fetchFromSupabase]);

  const handleExportCSV = useCallback((report: SavedReport) => {
    const csv = generateCSV(exportRows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [exportRows]);

  const handleCopyTSV = useCallback((_report: SavedReport) => {
    const tsv = generateTSV(exportRows);
    navigator.clipboard.writeText(tsv).catch(() => {});
  }, [exportRows]);

  const handleDelete = useCallback((id: string) => {
    // Refused while a Smart Collection rule or a pool feed still reads this
    // query — say so instead of quietly unbinding them.
    const { error } = store.deleteReport(id);
    if (error) addNotification({ type: 'error', title: 'Report not deleted', message: error, duration: 6000 });
  }, [store, addNotification]);

  const handleApply = useCallback((report: SavedReport) => {
    store.recordAccess(report.id);
    onApplyReport(report);
    onClose();
  }, [store, onApplyReport, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-80 bg-[var(--bg-raised)] shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)]">
          <h2 className="text-sm font-bold text-[var(--text-pri)]">Saved Reports</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => store.createFolder({ name: 'New Folder' })}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              title="New folder"
            >
              <FolderPlus className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowCreateReport(true)}
              className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
              title="Save current view as report"
            >
              <Plus className="h-3.5 w-3.5" />
              Save view
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors ml-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-2">
          {/* Pinned */}
          {pinnedReports.length > 0 && (
            <div className="mb-2">
              <div className="px-4 py-1 text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest flex items-center gap-1">
                <Star className="h-3 w-3 text-[var(--mustard)]" />
                Pinned
              </div>
              {pinnedReports.map((r) => (
                <ReportRow
                  key={r.id}
                  report={r}
                  onApply={() => handleApply(r)}
                  onExportCSV={() => handleExportCSV(r)}
                  onCopyTSV={() => handleCopyTSV(r)}
                  onDelete={() => handleDelete(r.id)}
                  onTogglePin={() => store.updateReport(r.id, { isPinned: !r.isPinned })}
                  onLinkSheets={() => setLinkSheetsForId(r.id)}
                />
              ))}
              <div className="mx-4 border-t border-[var(--rule)] my-2" />
            </div>
          )}

          {/* Folders */}
          {rootFolders.map((folder) => {
            const childFolders = store.getChildFolders(folder.id);
            const folderReports = store.getReportsInFolder(folder.id);
            return (
              <FolderSection
                key={folder.id}
                folder={folder}
                reports={folderReports}
                childFolders={childFolders}
                depth={0}
                onApply={handleApply}
                onExportCSV={handleExportCSV}
                onCopyTSV={handleCopyTSV}
                onDeleteReport={handleDelete}
                onTogglePin={(id) => {
                  const r = store.getReportById(id);
                  if (r) store.updateReport(id, { isPinned: !r.isPinned });
                }}
                onLinkSheets={(id) => setLinkSheetsForId(id)}
                onRenameFolder={store.renameFolder}
                onDeleteFolder={store.deleteFolder}
              />
            );
          })}

          {/* Root-level reports (no folder) */}
          {rootReports.length > 0 && (
            <div className={rootFolders.length > 0 ? 'border-t border-[var(--rule)] pt-2 mt-1' : ''}>
              {rootFolders.length > 0 && (
                <div className="px-4 py-1 text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest">Unsorted</div>
              )}
              {rootReports.map((r) => (
                <ReportRow
                  key={r.id}
                  report={r}
                  onApply={() => handleApply(r)}
                  onExportCSV={() => handleExportCSV(r)}
                  onCopyTSV={() => handleCopyTSV(r)}
                  onDelete={() => handleDelete(r.id)}
                  onTogglePin={() => store.updateReport(r.id, { isPinned: !r.isPinned })}
                  onLinkSheets={() => setLinkSheetsForId(r.id)}
                />
              ))}
            </div>
          )}

          {/* Empty state */}
          {rootFolders.length === 0 && rootReports.length === 0 && (
            <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
              <FileText className="h-10 w-10 text-[var(--text-ter)] mb-3" />
              <p className="text-sm font-semibold text-[var(--text-ter)] mb-1">No saved reports yet</p>
              <p className="text-xs text-[var(--text-ter)] mb-4">
                Apply filters, set your chart view, and click <strong>Save view</strong> to create a reusable report.
              </p>
              <button
                onClick={() => setShowCreateReport(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Save current view
              </button>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2.5 border-t border-[var(--rule)] bg-[var(--surface)]/50">
          <p className="text-[10px] text-[var(--text-ter)] leading-relaxed">
            Reports capture filters, sort, columns, and chart view.
            Use <strong>"Copy for Google Sheets"</strong> to paste data directly into a spreadsheet.
          </p>
        </div>
      </div>

      {/* Modals */}
      {showCreateReport && (
        <CreateReportModal
          folders={[...rootFolders, ...store.folders.filter((f) => !!f.parentId)]}
          onConfirm={(name, description, folderId, visibility) => {
            void store
              .createReportPersisted({
                name,
                description,
                folderId,
                filterGroups: currentState.filterGroups,
                queryText: currentState.queryText,
                queryAst: currentState.queryAst,
                sort: currentState.sort,
                groupBy: currentState.groupBy,
                visibleColumns: currentState.visibleColumns,
                activeChart: currentState.activeChart,
                visibility,
              })
              .then(({ error }) => {
                addNotification(error
                  ? { type: 'error', title: 'Report not saved', message: error, duration: 6000 }
                  : { type: 'success', title: 'Report saved', message: `"${name}" saved. ${audienceSentence(visibility)}`, duration: 4000 });
              });
            setShowCreateReport(false);
          }}
          onCancel={() => setShowCreateReport(false)}
        />
      )}

      {linkSheetsForId && (
        <LinkSheetsModal
          onConfirm={(url) => {
            store.linkToSheets(linkSheetsForId, url);
            setLinkSheetsForId(null);
          }}
          onCancel={() => setLinkSheetsForId(null)}
        />
      )}
    </>
  );
}
